<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- ── DESKTOP HEADER ── -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Banks</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="loading" class="inline-block w-24 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
            <span v-else>{{ banks.length }} bank account{{ banks.length !== 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button @click="openAdd"
          class="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-indigo-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          Add Bank
        </button>
      </div>

      <!-- ── MOBILE HEADER ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Banks</h1>
              <p class="text-indigo-200 text-xs mt-0.5">
                <span v-if="loading">Loading...</span>
                <span v-else>{{ banks.length }} account{{ banks.length !== 1 ? 's' : '' }}</span>
              </p>
            </div>
            <button @click="openAdd"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
          <!-- Stats -->
          <div v-if="!loading" :class="mobileStatCols" class="grid gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-indigo-200 uppercase tracking-widest mb-1">Banks</p>
              <p class="text-sm font-bold text-white">{{ banks.length }}</p>
            </div>
            <div v-if="hasPkr" class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-sky-200 uppercase tracking-widest mb-1">PKR</p>
              <p class="text-[10px] font-bold text-white">
                <span class="whitespace-nowrap">Rs {{ Math.floor(pkrTotal).toLocaleString('en-PK') }}</span>
              </p>
            </div>
            <div v-if="hasUsd" class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">USD</p>
              <p class="text-[10px] font-bold text-white">
                <span class="whitespace-nowrap">$ {{ usdTotal.toFixed(2) }}</span>
              </p>
            </div>
            <div v-if="hasEuro" class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-violet-200 uppercase tracking-widest mb-1">EUR</p>
              <p class="text-[10px] font-bold text-white">
                <span class="whitespace-nowrap">€ {{ euroTotal.toFixed(2) }}</span>
              </p>
            </div>
            <div v-if="hasMultipleCurrencies" class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-amber-200 uppercase tracking-widest mb-1">Total PKR</p>
              <p class="text-[10px] font-bold text-white">
                <span class="whitespace-nowrap">₨ {{ Math.floor(totalInPkr).toLocaleString('en-PK') }}</span>
              </p>
            </div>
          </div>
          <div v-else class="grid grid-cols-4 gap-2 mb-2">
            <div v-for="i in 4" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Card pulls over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <!-- Search input -->
          <div class="relative">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search banks..."
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- ── DESKTOP: search + stats strip ── -->
      <div class="hidden md:flex items-center gap-3 mb-3">
        <div class="relative w-56 shrink-0">
          <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search..." :disabled="loading"
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl pl-10 pr-10 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition disabled:opacity-50" />
          <button v-if="search" @click="search = ''"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="flex-1"></div>
        <div class="hidden md:flex items-center gap-2">
          <template v-if="loading">
            <div v-for="i in 3" :key="i" class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
          </template>
          <template v-else>
            <div v-if="hasPkr" class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-sky-500 uppercase tracking-wide">PKR Balance</p>
              <p class="text-sm font-bold text-slate-700 dark:text-slate-300">Rs {{ formatNum(pkrTotal) }}</p>
            </div>
            <div v-if="hasUsd" class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide">USD Balance</p>
              <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">$ {{ formatNum(usdTotal) }}</p>
            </div>
            <div v-if="hasEuro" class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-violet-500 uppercase tracking-wide">EUR Balance</p>
              <p class="text-sm font-bold text-violet-600 dark:text-violet-400">€ {{ formatNum(euroTotal) }}</p>
            </div>
            <div v-if="hasMultipleCurrencies" class="bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-amber-500 uppercase tracking-wide">Total in PKR</p>
              <p class="text-sm font-bold text-amber-700 dark:text-amber-400">₨ {{ formatNum(totalInPkr) }}</p>
            </div>
          </template>
        </div>
      </div>

      <!-- ── FLASH ── -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>
      <div v-if="$page.props.flash?.alert || errors?.length"
        class="mb-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-if="$page.props.flash?.alert">• {{ $page.props.flash.alert }}</p>
        <p v-for="e in (errors || [])" :key="e">• {{ e }}</p>
      </div>

      <!-- ── SKELETON ── -->
      <template v-if="loading">
        <!-- Desktop -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-6">
            <div v-for="i in 5" :key="i" class="h-2.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 6" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-9 w-9 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse shrink-0"></div>
            <div class="h-3 w-36 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse flex-1"></div>
            <div class="h-5 w-12 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
          </div>
        </div>
        <!-- Mobile -->
        <div class="md:hidden space-y-3">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 space-y-3">
            <div class="flex items-center justify-between">
              <div class="h-4 w-36 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-7 w-14 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div v-for="j in 2" :key="j" class="h-14 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── EMPTY STATE ── -->
      <div v-else-if="filtered.length === 0"
        class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ search ? 'No banks match your search' : 'No banks yet' }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ search ? 'Try a different keyword' : 'Add your first bank account to get started' }}
        </p>
        <button v-if="!search" @click="openAdd" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">+ Add Bank</button>
        <button v-else @click="search = ''" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">Clear search</button>
      </div>

      <!-- ── DESKTOP TABLE ── -->
      <div v-else class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-100 dark:border-slate-800">
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-10">#</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Bank Name</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Currency</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Balance</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Added</th>
              <th class="text-center px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(bank, i) in filtered" :key="bank.id">
              <!-- Main row -->
              <tr class="border-t border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer"
                @click="toggleExpand(bank.id)">
                <td class="px-5 py-4 text-slate-400 text-xs">
                  <svg class="w-3.5 h-3.5 transition-transform duration-200"
                    :class="expandedId === bank.id ? 'rotate-90 text-indigo-500' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                  </svg>
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center gap-2.5">
                    <div class="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 text-sm font-bold"
                      :class="currencyStyle(bank.currency).bg">
                      {{ currencySymbol(bank.currency) }}
                    </div>
                    <span class="font-semibold text-slate-800 dark:text-white">{{ bank.name }}</span>
                  </div>
                </td>
                <td class="px-5 py-4">
                  <span class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold"
                    :class="currencyStyle(bank.currency).badge">
                    {{ currencyLabel(bank.currency) }}
                  </span>
                </td>
                <td class="px-5 py-4 text-right font-semibold text-slate-700 dark:text-slate-300">
                  <span v-if="bank.account_type === 'credit'">
                    {{ currencySymbol(bank.currency) }} {{ formatNum(bank.used_amount) }} / {{ formatNum(bank.credit_limit) }}
                  </span>
                  <span v-else>
                    {{ currencySymbol(bank.currency) }} {{ formatNum(bank.total_balance) }}
                  </span>
                </td>
                <td class="px-5 py-4 text-slate-400 text-xs">{{ bank.created_at }}</td>
                <td class="px-5 py-4 text-center" @click.stop>
                  <div class="flex items-center justify-center gap-1">
                    <button @click="openEdit(bank)"
                      class="inline-flex items-center justify-center w-8 h-8 text-slate-500 dark:text-slate-400
                             hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                      </svg>
                    </button>
                    <button @click="openDeleteConfirm(bank)"
                      class="inline-flex items-center justify-center w-8 h-8 text-red-400
                             hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <!-- Expanded details row -->
              <tr v-if="expandedId === bank.id" class="border-t border-indigo-100 dark:border-indigo-900/30 bg-indigo-50/30 dark:bg-indigo-950/10">
                <td colspan="6" class="px-5 py-4">
                  <!-- Credit Card View -->
                  <div v-if="bank.account_type === 'credit'" class="grid grid-cols-3 gap-4 max-w-6xl">
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-slate-400 uppercase tracking-wide mb-2">Credit Limit</p>
                      <p class="font-semibold text-slate-700 dark:text-slate-300">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.credit_limit) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-rose-500 uppercase tracking-wide mb-2">Used Amount</p>
                      <p class="font-semibold text-rose-600 dark:text-rose-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.used_amount) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-emerald-500 uppercase tracking-wide mb-2">Available Credit</p>
                      <p class="font-semibold text-emerald-600 dark:text-emerald-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.available_credit) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-amber-500 uppercase tracking-wide mb-2">Expenses</p>
                      <p class="font-semibold text-amber-600 dark:text-amber-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.expense_total) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-cyan-500 uppercase tracking-wide mb-2">Payments Made</p>
                      <p class="font-semibold text-cyan-600 dark:text-cyan-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.income_total) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-slate-400 uppercase tracking-wide mb-2">Starting Balance</p>
                      <p class="font-semibold text-slate-700 dark:text-slate-300">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.opening_balance) }}</p>
                    </div>
                  </div>
                  <!-- Debit Account View -->
                  <div v-else class="grid grid-cols-3 gap-4 max-w-6xl">
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-slate-400 uppercase tracking-wide mb-2">Opening Balance</p>
                      <p class="font-semibold text-slate-700 dark:text-slate-300">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.opening_balance) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-emerald-500 uppercase tracking-wide mb-2">+ Incomes</p>
                      <p class="font-semibold text-emerald-600 dark:text-emerald-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.income_total) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-rose-500 uppercase tracking-wide mb-2">− Expenses</p>
                      <p class="font-semibold text-rose-600 dark:text-rose-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.expense_total) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-amber-500 uppercase tracking-wide mb-2">− Khata Sent</p>
                      <p class="font-semibold text-amber-600 dark:text-amber-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.khata_sent_total || 0) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-cyan-500 uppercase tracking-wide mb-2">+ Khata Received</p>
                      <p class="font-semibold text-cyan-600 dark:text-cyan-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.khata_received_total || 0) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
                      <p class="text-xs text-indigo-500 uppercase tracking-wide mb-2">Total Balance</p>
                      <p class="font-semibold text-indigo-600 dark:text-indigo-400">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.total_balance) }}</p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              <td colspan="6" class="px-5 py-3.5">
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Total · {{ filtered.length }} account{{ filtered.length !== 1 ? 's' : '' }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── MOBILE CARDS ── -->
      <div v-if="!loading" class="md:hidden space-y-3">
        <div v-for="(bank, i) in filtered" :key="bank.id"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <!-- Card header -->
          <div class="flex items-center justify-between px-4 py-3.5 cursor-pointer" @click="toggleExpand(bank.id)">
            <div class="flex items-center gap-2.5 min-w-0">
              <svg class="w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200"
                :class="expandedId === bank.id ? 'rotate-90 text-indigo-500' : ''"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
              <div class="min-w-0">
                <p class="font-semibold text-slate-800 dark:text-white truncate">{{ bank.name }}</p>
                <p class="text-xs mt-0.5" :class="currencyStyle(bank.currency).text">
                  {{ currencyLabel(bank.currency) }}
                </p>
              </div>
            </div>
            <div class="text-right shrink-0 ml-3">
              <p class="text-sm font-bold text-slate-700 dark:text-slate-300">
                <span v-if="bank.account_type === 'credit'">
                  {{ currencySymbol(bank.currency) }} {{ formatNum(bank.used_amount) }}
                </span>
                <span v-else>
                  {{ currencySymbol(bank.currency) }} {{ formatNum(bank.total_balance) }}
                </span>
              </p>
              <p class="text-[10px] text-slate-400 mt-0.5">
                {{ bank.account_type === 'credit' ? 'used' : 'balance' }}
              </p>
            </div>
          </div>

          <!-- Expanded details -->
          <div v-if="expandedId === bank.id"
            class="border-t border-indigo-100 dark:border-indigo-900/30 bg-indigo-50/30 dark:bg-indigo-950/10 p-4 space-y-3">
            <!-- Balance breakdown -->
            <div class="grid grid-cols-2 gap-2">
              <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Opening Balance</p>
                <p class="font-semibold text-slate-700 dark:text-slate-300 text-xs">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.opening_balance) }}</p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                <p class="text-[10px] text-emerald-500 uppercase tracking-wide mb-1">+ Incomes</p>
                <p class="font-semibold text-emerald-600 dark:text-emerald-400 text-xs">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.income_total) }}</p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                <p class="text-[10px] text-rose-500 uppercase tracking-wide mb-1">− Expenses</p>
                <p class="font-semibold text-rose-600 dark:text-rose-400 text-xs">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.expense_total) }}</p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                <p class="text-[10px] text-amber-500 uppercase tracking-wide mb-1">− Khata Sent</p>
                <p class="font-semibold text-amber-600 dark:text-amber-400 text-xs">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.khata_sent_total || 0) }}</p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                <p class="text-[10px] text-cyan-500 uppercase tracking-wide mb-1">+ Khata Received</p>
                <p class="font-semibold text-cyan-600 dark:text-cyan-400 text-xs">{{ currencySymbol(bank.currency) }} {{ formatNum(bank.khata_received_total || 0) }}</p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Added On</p>
                <p class="font-semibold text-slate-700 dark:text-slate-300 text-xs">{{ bank.created_at }}</p>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="grid grid-cols-2 gap-2">
              <button @click.stop="openEdit(bank)"
                class="flex items-center justify-center gap-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400
                       bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 rounded-xl py-2.5 transition active:scale-95">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                </svg>
                Edit
              </button>
              <button @click.stop="openDeleteConfirm(bank)"
                class="flex items-center justify-center gap-1.5 text-sm font-semibold text-red-600 dark:text-red-400
                       bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-xl py-2.5 transition active:scale-95">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- ── ADD MODAL ── -->
    <Modal v-if="showAdd" @close="closeAdd">
      <template #title>Add Bank</template>
      <form @submit.prevent="submitAdd" class="space-y-4">
        <InputField v-model="addForm.name" label="Bank Name" placeholder="e.g. HBL Current Account" required />

        <!-- Opening Balance -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            {{ addForm.account_type === 'credit' ? 'Starting Balance (if any)' : 'Opening Balance' }}
          </label>
          <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 transition">
            <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">{{ currencySymbol(addForm.currency) }}</span>
            <input v-model="addForm.opening_balance" type="number" step="0.01" placeholder="0.00"
              class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
          </div>
        </div>

        <!-- Credit Limit (only for credit cards) -->
        <div v-if="addForm.account_type === 'credit'">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Credit Limit</label>
          <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 transition">
            <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">{{ currencySymbol(addForm.currency) }}</span>
            <input v-model="addForm.credit_limit" type="number" step="0.01" min="0.01" placeholder="e.g. 100000.00" required
              class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
          </div>
        </div>

        <!-- More Details Toggle -->
        <button type="button" @click="showAddMoreDetails = !showAddMoreDetails"
          class="flex items-center gap-2 text-sm font-medium text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition">
          <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': showAddMoreDetails }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
          {{ showAddMoreDetails ? 'Hide Details' : 'More Details' }}
        </button>

        <!-- Additional Fields (collapsible) -->
        <div v-show="showAddMoreDetails" class="space-y-4 pt-2">
          <!-- Account Type picker -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Account Type</label>
            <div class="grid grid-cols-2 gap-2">
              <button type="button"
                @click="addForm.account_type = 'debit'"
                class="flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition"
                :class="addForm.account_type === 'debit'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
                <span class="text-lg">💳</span>
                <span class="text-xs">Debit</span>
              </button>
              <button type="button"
                @click="addForm.account_type = 'credit'"
                class="flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition"
                :class="addForm.account_type === 'credit'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
                <span class="text-lg">💳</span>
                <span class="text-xs">Credit</span>
              </button>
            </div>
          </div>

          <!-- Currency picker -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Currency</label>
            <div class="grid grid-cols-3 gap-2">
              <button v-for="c in currencies" :key="c.value" type="button"
                @click="addForm.currency = c.value; if (c.value === 'pkr') addForm.rate_in_pkr = 1"
                class="flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition"
                :class="addForm.currency === c.value
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
                <span class="text-lg">{{ c.symbol }}</span>
                <span class="text-xs">{{ c.label }}</span>
              </button>
            </div>
          </div>

          <!-- Rate in PKR -->
          <div v-if="addForm.currency !== 'pkr'">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">1 {{ currencyLabel(addForm.currency) }} = ? PKR</label>
            <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 transition">
              <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">₨</span>
              <input v-model="addForm.rate_in_pkr" type="number" step="0.01" min="0.01" placeholder="e.g. 278.50" required
                class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
            </div>
          </div>
        </div>

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeAdd"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="addLoading"
            class="flex-1 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold transition">
            {{ addLoading ? 'Saving...' : 'Add Bank' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── EDIT MODAL ── -->
    <Modal v-if="showEdit" @close="closeEdit">
      <template #title>Edit — {{ editTarget?.name }}</template>
      <form @submit.prevent="submitEdit" class="space-y-4">
        <InputField v-model="editForm.name" label="Bank Name" required />

        <!-- Opening Balance -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            {{ editForm.account_type === 'credit' ? 'Starting Balance (if any)' : 'Opening Balance' }}
          </label>
          <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 transition">
            <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">{{ currencySymbol(editForm.currency) }}</span>
            <input v-model="editForm.opening_balance" type="number" step="0.01" placeholder="0.00"
              class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
          </div>
        </div>

        <!-- Credit Limit (only for credit cards) -->
        <div v-if="editForm.account_type === 'credit'">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Credit Limit</label>
          <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 transition">
            <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">{{ currencySymbol(editForm.currency) }}</span>
            <input v-model="editForm.credit_limit" type="number" step="0.01" min="0.01" placeholder="e.g. 100000.00" required
              class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
          </div>
        </div>

        <!-- More Details Toggle -->
        <button type="button" @click="showEditMoreDetails = !showEditMoreDetails"
          class="flex items-center gap-2 text-sm font-medium text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition">
          <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': showEditMoreDetails }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
          {{ showEditMoreDetails ? 'Hide Details' : 'More Details' }}
        </button>

        <!-- Additional Fields (collapsible) -->
        <div v-show="showEditMoreDetails" class="space-y-4 pt-2">
          <!-- Account Type picker -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Account Type</label>
            <div class="grid grid-cols-2 gap-2">
              <button type="button"
                @click="editForm.account_type = 'debit'"
                class="flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition"
                :class="editForm.account_type === 'debit'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
                <span class="text-lg">💳</span>
                <span class="text-xs">Debit</span>
              </button>
              <button type="button"
                @click="editForm.account_type = 'credit'"
                class="flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition"
                :class="editForm.account_type === 'credit'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
                <span class="text-lg">💳</span>
                <span class="text-xs">Credit</span>
              </button>
            </div>
          </div>

          <!-- Currency picker -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Currency</label>
            <div class="grid grid-cols-3 gap-2">
              <button v-for="c in currencies" :key="c.value" type="button"
                @click="editForm.currency = c.value; if (c.value === 'pkr') editForm.rate_in_pkr = 1"
                class="flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition"
                :class="editForm.currency === c.value
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'">
                <span class="text-lg">{{ c.symbol }}</span>
                <span class="text-xs">{{ c.label }}</span>
              </button>
            </div>
          </div>

          <!-- Rate in PKR -->
          <div v-if="editForm.currency !== 'pkr'">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">1 {{ currencyLabel(editForm.currency) }} = ? PKR</label>
            <div class="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 transition">
              <span class="px-3 text-sm font-semibold text-slate-400 dark:text-slate-500 shrink-0">₨</span>
              <input v-model="editForm.rate_in_pkr" type="number" step="0.01" min="0.01" placeholder="e.g. 278.50" required
                class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-400 py-3 pr-4 text-sm focus:outline-none" />
            </div>
          </div>
        </div>

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeEdit"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="editLoading"
            class="flex-1 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold transition">
            {{ editLoading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── DELETE CONFIRM ── -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Delete Bank?"
      :message="`Are you sure you want to delete '${deleteTarget?.name}'? This action cannot be undone.`"
      info-message="Deleting this bank won't affect any existing invoices, payments, or reports. All historical data will remain intact."
      confirm-text="Yes, Delete"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />

  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useForm, usePage, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import InputField from '@/components/InputField.vue'

const page         = usePage()
const errors       = computed(() => page.props.errors)
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

const banks   = ref([])
const loading = ref(true)

// ── Currency config ──────────────────────────────────────
const currencies = [
  { value: 'pkr',  label: 'PKR', symbol: '₨' },
  { value: 'usd',  label: 'USD', symbol: '$'  },
  { value: 'euro', label: 'EUR', symbol: '€'  },
]

function currencySymbol(c) { return currencies.find(x => x.value === c)?.symbol ?? c }
function currencyLabel(c)  { return currencies.find(x => x.value === c)?.label ?? c.toUpperCase() }
function currencyStyle(c) {
  if (c === 'pkr')  return { bg: 'bg-sky-100 dark:bg-sky-950 text-sky-600 dark:text-sky-400',         badge: 'bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-400',         text: 'text-sky-500' }
  if (c === 'usd')  return { bg: 'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400', text: 'text-emerald-500' }
  if (c === 'euro') return { bg: 'bg-violet-100 dark:bg-violet-950 text-violet-600 dark:text-violet-400',     badge: 'bg-violet-50 dark:bg-violet-950 text-violet-600 dark:text-violet-400',     text: 'text-violet-500' }
  return { bg: 'bg-slate-100 dark:bg-slate-800 text-slate-500', badge: 'bg-slate-100 text-slate-500', text: 'text-slate-400' }
}

// ── Stats ────────────────────────────────────────────────
// Helper function to get balance for both debit and credit accounts
function getBalance(bank) {
  if (bank.account_type === 'credit') {
    // For credit cards, show available credit as the "balance"
    return bank.available_credit || 0
  }
  return bank.total_balance || 0
}

const pkrTotal  = computed(() => banks.value.filter(b => b.currency === 'pkr').reduce((s, b) => s + getBalance(b), 0))
const usdTotal  = computed(() => banks.value.filter(b => b.currency === 'usd').reduce((s, b) => s + getBalance(b), 0))
const euroTotal = computed(() => banks.value.filter(b => b.currency === 'euro').reduce((s, b) => s + getBalance(b), 0))

const hasPkr  = computed(() => banks.value.some(b => b.currency === 'pkr'))
const hasUsd  = computed(() => banks.value.some(b => b.currency === 'usd'))
const hasEuro = computed(() => banks.value.some(b => b.currency === 'euro'))

const activeCurrencyCount = computed(() => [hasPkr.value, hasUsd.value, hasEuro.value].filter(Boolean).length)
const hasMultipleCurrencies = computed(() => activeCurrencyCount.value > 1)
const totalInPkr = computed(() => banks.value.reduce((s, b) => s + getBalance(b) * (b.rate_in_pkr ?? 1), 0))

const mobileStatCols = computed(() => {
  const count = 1 + (hasPkr.value ? 1 : 0) + (hasUsd.value ? 1 : 0) + (hasEuro.value ? 1 : 0) + (hasMultipleCurrencies.value ? 1 : 0)
  if (count <= 2) return 'grid-cols-2'
  if (count === 3) return 'grid-cols-3'
  if (count === 4) return 'grid-cols-4'
  return 'grid-cols-3'
})

// ── Search ───────────────────────────────────────────────
const search = ref('')
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return q ? banks.value.filter(b => b.name.toLowerCase().includes(q)) : [...banks.value]
})

// ── Expand ───────────────────────────────────────────────
const expandedId = ref(null)
function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

// ── Fetch ────────────────────────────────────────────────
async function fetchData() {
  loading.value = true
  try {
    const res  = await fetch('/banks/data', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const json = await res.json()
    banks.value = json.banks || []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  document.title = `Banks | ${businessName.value}`
  fetchData()
})

// ── Utilities ────────────────────────────────────────────
function formatNum(n) {
  return Number(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// ── Add ──────────────────────────────────────────────────
const showAdd   = ref(false)
const addLoading = ref(false)
const showAddMoreDetails = ref(false)
const addForm   = ref({ name: '', currency: 'pkr', account_type: 'debit', opening_balance: '', rate_in_pkr: 1, credit_limit: '' })

function openAdd()  { showAdd.value = true }
function closeAdd() {
  showAdd.value = false
  showAddMoreDetails.value = false
  addForm.value = { name: '', currency: 'pkr', account_type: 'debit', opening_balance: '', rate_in_pkr: 1, credit_limit: '' }
}

function submitAdd() {
  addLoading.value = true
  const payload = {
    ...addForm.value,
    opening_balance: parseFloat(addForm.value.opening_balance) || 0,
    rate_in_pkr: addForm.value.currency === 'pkr' ? 1 : (parseFloat(addForm.value.rate_in_pkr) || 1),
    credit_limit: addForm.value.account_type === 'credit' ? (parseFloat(addForm.value.credit_limit) || 0) : 0
  }
  useForm({ bank: payload }).post('/banks', {
    onSuccess: () => { closeAdd(); fetchData() },
    onFinish:  () => { addLoading.value = false },
  })
}

// ── Edit ─────────────────────────────────────────────────
const showEdit    = ref(false)
const editLoading = ref(false)
const showEditMoreDetails = ref(false)
const editTarget  = ref(null)
const editForm    = ref({ name: '', currency: 'pkr', account_type: 'debit', opening_balance: '', rate_in_pkr: 1, credit_limit: '' })

function openEdit(bank) {
  editTarget.value = bank
  editForm.value   = {
    name: bank.name,
    currency: bank.currency,
    account_type: bank.account_type || 'debit',
    opening_balance: bank.opening_balance,
    rate_in_pkr: bank.rate_in_pkr ?? 1,
    credit_limit: bank.credit_limit || ''
  }
  showEdit.value   = true
}
function closeEdit() {
  showEdit.value = false
  showEditMoreDetails.value = false
  editTarget.value = null
}

function submitEdit() {
  editLoading.value = true
  const payload = {
    ...editForm.value,
    opening_balance: parseFloat(editForm.value.opening_balance) || 0,
    rate_in_pkr: editForm.value.currency === 'pkr' ? 1 : (parseFloat(editForm.value.rate_in_pkr) || 1),
    credit_limit: editForm.value.account_type === 'credit' ? (parseFloat(editForm.value.credit_limit) || 0) : 0
  }
  useForm({ bank: payload }).patch(`/banks/${editTarget.value.id}`, {
    onSuccess: () => { closeEdit(); fetchData() },
    onFinish:  () => { editLoading.value = false },
  })
}

// ── Delete ───────────────────────────────────────────────
const showDeleteConfirm = ref(false)
const deleteTarget      = ref(null)

function openDeleteConfirm(bank) { deleteTarget.value = bank; showDeleteConfirm.value = true }
function closeDeleteConfirm()    { showDeleteConfirm.value = false; deleteTarget.value = null }

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`/banks/${deleteTarget.value.id}`, {
      onSuccess: () => { closeDeleteConfirm(); fetchData(); resolve() },
      onError:   () => { resolve() },
    })
  })
}
</script>
